export const Daycare_SOP = {
    daycareCheckIn: {
        title: "Morning Check-In Routine",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/daycareCheckInTitle.png",
        body: `
          <section>
            <h3>🌞 Start Strong: Smooth Mornings, Safe Play</h3>
            <p>
              The morning check-in sets the tone for the entire daycare day. At Pawsible, we make it <span class="highlight">efficient, warm, and detail-oriented</span> to ensure every pup is safe, accounted for, and ready to play.
            </p>
          </section>
      
          <section>
            <h4>📋 1. Greeting & Intake</h4>
            <ul>
              <li>👋 Greet the paw parent warmly and by name — build trust from the start</li>
              <li>📱 Confirm the pet’s name, pick-up time, and any special notes in the system</li>
              <li>💬 Ask if there are any updates since the last visit: new meds, behavior, health, or home changes</li>
            </ul>
            <p><span class="highlight-green">A friendly, informed check-in helps set a professional and caring tone for the day.</span></p>
          </section>
      
          <section>
            <h4>🐾 2. Health & Behavior Check</h4>
            <ul>
              <li>👁️ Perform a quick visual scan: eyes, coat, gait, attitude</li>
              <li>🫧 Check for visible fleas, injuries, or signs of illness — note anything unusual immediately</li>
              <li>📏 If the dog appears underweight, limping, coughing, or excessively nervous, notify a supervisor</li>
            </ul>
            <p><span class="highlight-red">Every dog must be healthy and non-contagious to enter playgroup</span>. If concerns arise, isolate the dog immediately and notify a manager for assessment.</p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/daycareCheckInImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🧠 3. Temperament & Group Fit Review</h4>
            <ul>
              <li>🐕 Review prior behavior notes or alerts in the profile</li>
              <li>🔄 If the dog is new, reactive, or high-energy, flag them for re-assessment or slow introduction</li>
              <li>👥 Place the dog in a holding area if needed before assigning them to a group</li>
            </ul>
          </section>
      
          <section>
            <h4>🗂️ 4. Logging & Handoff</h4>
            <ul>
              <li>📲 Mark the pet as “checked in” on the daycare dashboard or POS system</li>
              <li>📋 Record any special instructions in the day’s playgroup notes (e.g., “No group with Buddy today”)</li>
              <li>🧑‍🤝‍🧑 Communicate directly with the playgroup handler for any behavior, medical, or grouping notes</li>
            </ul>
            <p><span class="highlight-green">Clear logging avoids confusion and keeps everyone aligned from the start of the day.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Every check-in is a trust exchange between Pawsible and the pet parent.</span> 
              Prioritize consistency, clarity, and warmth to make the start of each day smooth, safe, and seamless.
            </p>
          </section>
        `
      },
      trialTemperamentTest: {
        title: "New Client Trial & Temperament Test",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/trialTemperamentTestTitle.png",
        body: `
          <section>
            <h3>🧠 Smart Screening Builds Safe Playgroups</h3>
            <p>
              Every new daycare guest at Pawsible must complete a <span class="highlight">Trial & Temperament Test</span> before being fully admitted to group play. 
              This ensures safety, emotional wellness, and the best experience for both pets and staff.
            </p>
          </section>
      
          <section>
            <h4>📝 1. Pre-Arrival Client Screening</h4>
            <ul>
              <li>📋 Require completion of intake forms: behavior history, vaccination records, health disclosures</li>
              <li>🔎 Look for red flags: history of dog aggression, resource guarding, severe separation anxiety, or unneutered status (if over 8–10 months)</li>
              <li>📞 Conduct a phone consultation if any concerns arise prior to scheduling trial day</li>
            </ul>
            <p><span class="highlight-blue">A strong pre-screen saves time, stress, and risk during the in-person trial.</span></p>
          </section>
      
          <section>
            <h4>🐾 2. Trial Day Arrival & Setup</h4>
            <ul>
              <li>👋 Greet the pet calmly and lead them to a neutral holding area — avoid overwhelming stimulation</li>
              <li>👀 Perform an initial visual health check (coat, gait, eyes, energy level)</li>
              <li>📝 Confirm owner notes again — any recent changes in behavior or health?</li>
              <li>⌛ Give 5–10 minutes of decompression time before introducing into the playgroup</li>
            </ul>
          </section>
      
          <section>
            <h4>🧠 3. Temperament Testing Steps</h4>
            <ul>
              <li>🐶 Introduce the dog to 1 calm, neutral dog first — observe body language (tail carriage, sniffing, stiffness, play solicitation)</li>
              <li>👥 Slowly add 1–2 additional stable dogs if appropriate, continuing to monitor stress signs or reactivity</li>
              <li>🔍 Watch for:
                <ul>
                  <li>✨ Positive signs: relaxed body, loose wagging tail, polite greetings, willingness to engage or disengage</li>
                  <li>🚩 Red flags: prolonged staring, stiff posturing, hard muzzle punches, escalated barking, lunging, or snapping</li>
                </ul>
              </li>
              <li>🛑 Immediately remove if any unsafe behavior is observed — safety overrides continuing trial.</li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/trialTemperamentTestImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
            <p><span class="highlight-red">If in doubt, err on the side of caution. One bad match can destabilize the group.</span></p>
          </section>
      
          <section>
            <h4>🗂️ 4. Trial Outcome & Documentation</h4>
            <ul>
              <li>📋 Complete a Trial Day Report outlining:
                <ul>
                  <li>✅ Passed — approved for full daycare</li>
                  <li>⏸️ Limited — approved for smaller group or short sessions only (retest later)</li>
                  <li>❌ Failed — not a fit for group play; recommend private or group training with evaluation</li>
                </ul>
              </li>
              <li>💬 Provide a verbal and/or written summary to the client same day</li>
              <li>📲 Update daycare dashboard with full temperament notes for future group assignments</li>
            </ul>
            <p><span class="highlight-green">Clear records prevent future mistakes and show professional transparency to paw parents.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">A careful trial process protects the pack, supports better experiences, and reduces incidents long-term.</span> 
              It's not just a formality — it's the foundation of a safe, joyful daycare culture.
            </p>
          </section>
        `
      },
      playgroupInfluencers: {
        title: "Playgroup Influencers & Canine Role Models",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/playgroupInfluencersTitle.png",
        body: `
          <section>
            <h3>🏆 Natural Leaders, Safer Groups</h3>
            <p>
              At Pawsible, leadership starts with us. <span class="highlight">Daycare staff set the tone</span> through calm, confident guidance — and we also recognize canine role models within the group to help maintain a smooth, happy environment. 
              When humans and dogs model good behavior together, playgroups stay balanced and safe.
            </p>
          </section>
      
          <section>
            <h4>🧑‍🏫 1. Staff Leadership: The First Pack Leader</h4>
            <ul>
              <li>🧘 Maintain calm, assertive body language — move with purpose, avoid frantic or passive movements</li>
              <li>👀 Stay present and proactive — active leadership keeps group energy balanced</li>
              <li>🗣️ Use consistent verbal markers ("Good job!" "Easy!" "Come on!") to guide the group flow</li>
              <li>🏔️ Set clear, firm, and fair boundaries without frustration or emotional escalation</li>
            </ul>
            <p><span class="highlight-green">The dogs mirror the staff — when you lead calmly, they follow calmly.</span></p>
          </section>
      
          <section>
            <h4>🐾 2. Identifying a Canine Playgroup Influencer</h4>
            <ul>
              <li>🌟 Displays neutral, friendly greetings without overexcitement</li>
              <li>🧠 Diffuses tension naturally or redirects rough play without escalation</li>
              <li>🎯 Adjusts to different play styles easily, without becoming overstimulated or bossy</li>
              <li>👥 Offers appropriate corrections gently, not harshly</li>
            </ul>
            <p><span class="highlight-blue">A true influencer promotes calmness, not chaos.</span></p>
          </section>
      
          <section>
            <h4>🔄 3. Using Influencers & Staff Leadership Together</h4>
            <ul>
              <li>🤝 Introduce new or shy dogs near strong canine influencers, guided calmly by staff</li>
              <li>🌈 Allow influencers to model good greetings and play — but staff maintain ultimate leadership over all group dynamics</li>
              <li>🎾 Reinforce both canine and human-led redirections with praise, movement, or brief breaks</li>
              <li>🔁 Rotate influencers to prevent exhaustion or social pressure on any one dog</li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/playgroupInfluencersImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
            <p><span class="highlight-green">Dogs feel safest when leadership is clear, consistent, and collaborative — both from staff and their canine peers.</span></p>
          </section>
      
          <section>
            <h4>⚠️ 4. Cautions with Role Models</h4>
            <ul>
              <li>🛑 If an influencer dog begins overcorrecting or controlling the group excessively, give them a rest break</li>
              <li>🧠 Staff must step in first to address tension — never allow dogs to "police" unsupervised</li>
              <li>⚖️ Balance all leadership with fairness, calmness, and neutral enforcement of rules</li>
            </ul>
            <p><span class="highlight-red">Staff lead first. Influencers support second. This keeps the structure healthy and safe.</span></p>
          </section>
      
          <section>
            <h4>📋 5. Communication & Recognition</h4>
            <ul>
              <li>🗂️ Log recognized canine influencers in the daycare dashboard under play style notes</li>
              <li>💬 Share shift updates about strong influencers and how the groups responded to them</li>
              <li>🏅 Celebrate influencer moments in daily report cards (“Charlie helped set the tone for calm play today!”)</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Great playgroups are built on leadership — from both humans and dogs.</span> 
              At Pawsible, we lead first with calm guidance, then amplify our success by empowering canine role models to help maintain a joyful, respectful pack.
            </p>
          </section>
        `
      },      
      positiveRedirection: {
        title: "Positive Reinforcement & Redirection Techniques",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/positiveRedirectionTitle.png",
        body: `
          <section>
            <h3>🎯 Lead With Encouragement, Redirect With Purpose</h3>
            <p>
              At Pawsible, we guide behavior through <span class="highlight">positive reinforcement and thoughtful redirection</span>. 
              Our role isn’t just to stop unwanted behavior — it's to <span class="highlight-green">build better habits and joyful play environments</span>.
            </p>
          </section>
      
          <section>
            <h4>🌟 1. Positive Reinforcement Basics</h4>
            <ul>
              <li>🐾 Praise calm greetings, polite play, healthy disengagement, and following staff guidance</li>
              <li>🗣️ Use upbeat verbal markers like “Good job!” or “Nice play!” paired with soft clapping or happy tone</li>
              <li>🎾 Reward with attention, access to a preferred toy, or movement toward favorite areas</li>
              <li>🧘 Reinforce calmness during high-energy moments — petting a relaxed dog can stabilize a group’s energy</li>
            </ul>
            <p><span class="highlight-blue">Catch good behavior early — don’t wait for problems to correct.</span></p>
          </section>
      
          <section>
            <h4>🔄 2. Redirection Techniques</h4>
            <ul>
              <li>👏 Use a single clap, whistle, or soft call to interrupt overly rough play</li>
              <li>🚶‍♂️ Guide a dog’s body away calmly — no grabbing, no yanking, no frustration</li>
              <li>🎯 Redirect attention with toys, a new playmate, movement across the yard, or calm verbal cues</li>
              <li>🍖 Reinforce successful redirection with praise once the dog disengages or adjusts behavior</li>
            </ul>
            <p><span class="highlight-green">Redirection isn’t punishment — it’s a leadership tool to shift energy and support better choices.</span></p>
          </section>
      
          <section>
            <h4>⚖️ 3. When to Step In Quickly</h4>
            <ul>
              <li>👊 Mounting or persistent roughness not corrected by other dogs</li>
              <li>👀 Intense staring, stalking, or tension in the body posture</li>
              <li>🛑 Overexcitement that escalates into chasing, slamming, or growling</li>
              <li>😟 Shy or overwhelmed dogs showing signs of shutdown (tail tuck, freezing, avoiding)</li>
            </ul>
            <p><span class="highlight-red">If escalation risk is visible, calmly step in before conflict can happen.</span></p>
          </section>
      
          <section>
            <h4>🧠 4. Building a Positive Play Culture</h4>
            <ul>
              <li>🏆 Celebrate dogs who naturally model good play — they set the tone for others</li>
              <li>🔁 Repeat and reward the behavior you want multiple times a session</li>
              <li>🗣️ Maintain consistent staff cues and vocabulary — dogs learn faster when everyone speaks the same "language"</li>
            </ul>
            <p><span class="highlight-blue">Consistency among staff builds reliable, confident, and safe dog groups.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Good daycare leadership isn't about control — it's about collaboration with the dogs in our care.</span> 
              Reward generously, redirect calmly, and build a daycare culture rooted in trust and positivity.
            </p>
          </section>
        `
      },      
      playgroupSorting: {
        title: "Playgroup Sorting & Safety",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/playgroupSortingTitle.png",
        body: `
          <section>
            <h3>📏 The Right Playgroup Makes All the Difference</h3>
            <p>
              At Pawsible, thoughtful <span class="highlight">playgroup sorting</span> is the key to safe, happy, and enriching daycare experiences. 
              Size alone doesn't determine a good match — we sort by energy, temperament, and play style to create the best possible group dynamics.
            </p>
          </section>
      
          <section>
            <h4>🐕 1. Core Sorting Criteria</h4>
            <ul>
              <li>📏 <strong>Size:</strong> Tiny dogs should not mix with large ones unless play styles and energy levels are perfectly compatible</li>
              <li>🧠 <strong>Temperament:</strong> Sort by confidence level, play tolerance, reactivity, and general sociability</li>
              <li>🎮 <strong>Play Style:</strong> Match wrestlers with wrestlers, chasers with chasers, and shy dogs with calmer companions</li>
              <li>🌀 <strong>Energy Level:</strong> Group high-energy dogs separately from lower-energy or senior dogs</li>
            </ul>
            <p><span class="highlight-green">Balance is key: No group should be dominated by one overly assertive or overly timid dog.</span></p>
          </section>
      
          <section>
            <h4>🔍 2. Sorting Best Practices</h4>
            <ul>
              <li>👀 Observe new dogs for at least 15 minutes before final group assignment</li>
              <li>🔄 Allow for flexible moves: it's okay to reassign dogs during the day if energy or dynamics shift</li>
              <li>🚪 Provide clear separation between groups (fences, visual barriers) to prevent overstimulation or barrier frustration</li>
              <li>👥 Always maintain appropriate staff-to-dog ratios depending on group size and temperament (respectfully 1 to 10)</li>
            </ul>
          </section>
      
          <section>
            <h4>🚨 3. Red Flags for Immediate Group Adjustment</h4>
            <ul>
              <li>👊 Excessive mounting, pinning, or bullying behavior</li>
              <li>🐍 Hard staring, guarding of toys, food, or staff</li>
              <li>🚫 Inability to disengage from high-arousal play</li>
              <li>🐾 Shy or nervous dogs becoming overwhelmed or defensive</li>
            </ul>
            <p><span class="highlight-red">If a dog shows signs of stress or instability, relocate calmly — don’t wait for escalation.</span></p>
          </section>
      
          <section>
            <h4>📋 4. Documentation & Communication</h4>
            <ul>
              <li>🗂️ Log any group changes, temperament notes, or concerns immediately in the daycare dashboard</li>
              <li>💬 Update fellow team members at shift changes about any sorting adjustments or dogs to monitor</li>
            </ul>
            <p><span class="highlight-blue">A well-informed team keeps the entire play environment smoother and safer for everyone.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Playgroup sorting is both an art and a science.</span> 
              Thoughtful matching keeps energy balanced, prevents injuries, and makes daycare a joyful experience for every guest.
            </p>
          </section>
        `
      },      
      groupManagement: {
        title: "Group Management During Play",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/groupManagementTitle.png",
        body: `
          <section>
            <h3>👀 Active Eyes, Calm Hands</h3>
            <p>
              Managing a playgroup at Pawsible means <span class="highlight">leading proactively, observing keenly, and intervening calmly</span>. 
              Our goal is to keep the environment safe, balanced, and fun — always with minimal stress for the dogs and the staff.
            </p>
          </section>
      
          <section>
            <h4>🧠 1. Key Roles of the Playgroup Leader</h4>
            <ul>
              <li>👀 Constantly monitor all dogs — no distracted standing or sitting unless actively observing</li>
              <li>🐕 Interact frequently: moving through the group, offering calm redirections, and supporting positive play</li>
              <li>🎯 Identify rising energy early and diffuse it before it escalates</li>
              <li>🧹 Maintain a clean, hazard-free play area (e.g., pick up toys if they trigger resource guarding)</li>
            </ul>
            <p><span class="highlight-green">Active engagement prevents 90% of potential incidents before they happen.</span></p>
          </section>
      
          <section>
            <h4>📚 2. Core Play Monitoring Skills</h4>
            <ul>
              <li>🧠 Know the difference between healthy play (role switching, soft mouths, loose bodies) and problematic play (relentless chasing, body slamming, hard stares)</li>
              <li>🔄 Allow natural corrections between dogs but step in at the first sign of escalation</li>
              <li>🛑 Be ready to calmly split up play if energy gets too high, using body blocks, recalls, or leashes if necessary</li>
            </ul>
          </section>
      
          <section>
            <h4>🔄 3. Redirection Techniques</h4>
            <ul>
              <li>👏 Clap once or make a soft vocal cue to redirect when play gets too rough</li>
              <li>🎾 Redirect attention with a toy, treat, or movement to shift energy</li>
              <li>🚶‍♂️ Calmly leash and walk out any dog that repeatedly disregards redirections — cooling-off time matters</li>
              <li>🧘 Stay neutral: no yelling, grabbing, or fast, startling movements unless necessary for safety</li>
            </ul>
            <p><span class="highlight-blue">Firm, fair, and neutral leadership builds respect and trust in the playgroup.</span></p>
          </section>
      
          <section>
            <h4>🚨 4. Managing Special Situations</h4>
            <ul>
              <li>👶 Puppies: Monitor for overwhelming or excessive correction by adults</li>
              <li>🐕‍🦺 Senior dogs: Protect them from high-energy bumps or rough players</li>
              <li>🌡️ Hot weather: Watch for heat exhaustion signs — mandatory hydration breaks</li>
              <li>🥶 Cold weather: Provide access to warm areas if needed for short-coated or senior dogs</li>
            </ul>
          </section>
      
          <section>
            <h4>🗂️ 5. Communication & Handoffs</h4>
            <ul>
              <li>📋 Update the next handler at shift change with important notes: tired dogs, grumpy moods, standout players</li>
              <li>📲 Log any incidents, redirections, or unusual behaviors immediately in the daycare dashboard</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Playgroup management isn't passive — it's a dance between leadership, empathy, and quick thinking.</span> 
              Be present, be proactive, and keep every tail wagging safely.
            </p>
          </section>
        `
      },
      playStyleTracking: {
        title: "Play Style Identification & Tracking",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/playStyleTrackingTitle.png",
        body: `
          <section>
            <h3>🎮 Know the Players, Shape the Game</h3>
            <p>
              Not all dogs play the same way — and recognizing their <span class="highlight">individual play styles</span> is crucial for safe, happy, and compatible group dynamics. 
              At Pawsible, we <span class="highlight-green">observe, log, and adjust</span> based on each dog’s natural preferences and energy.
            </p>
          </section>
      
          <section>
            <h4>🔍 1. Common Play Styles</h4>
            <ul>
              <li>🤼 <strong>Wrestlers:</strong> Love body-to-body play, pinning, and rolling — physical but friendly</li>
              <li>🏃 <strong>Chasers:</strong> Love running, being chased, or chasing others — often vocal but playful</li>
              <li>🧘 <strong>Gentle Players:</strong> Prefer soft greetings, parallel play, or slow-motion games</li>
              <li>🌪️ <strong>Rough Players:</strong> Engage in intense, high-arousal play — requires close monitoring and calm peers</li>
              <li>👀 <strong>Observers:</strong> Prefer to watch or engage minimally — may join when comfortable</li>
            </ul>
            <p><span class="highlight-blue">Knowing who’s who prevents mismatches and reduces conflict before it starts.</span></p>
          </section>
      
          <section>
            <h4>🧠 2. How to Identify a Dog's Play Style</h4>
            <ul>
              <li>👀 Observe first 10–15 minutes of free play before making assumptions</li>
              <li>🔄 Watch how the dog initiates and responds to invitations from others</li>
              <li>🐕 Note if energy escalates or self-regulates during play interactions</li>
              <li>📅 Recognize that play styles can change with age, confidence, or mood</li>
            </ul>
            <p><span class="highlight-green">Play style is a living profile — not a permanent label.</span></p>
          </section>
      
          <section>
            <h4>🗂️ 3. Tracking and Updating Play Styles</h4>
            <ul>
              <li>📲 Record primary and secondary play styles in the daycare dashboard</li>
              <li>📝 Update notes if play patterns shift over time (e.g., a shy dog becomes a chaser!)</li>
              <li>👥 Share notable play style changes at team meetings or shift changes</li>
              <li>📋 Flag special considerations (e.g., “prefers low-contact play,” “needs slow introductions”)</li>
            </ul>
          </section>
      
          <section>
            <h4>⚖️ 4. Applying Play Style Knowledge to Group Management</h4>
            <ul>
              <li>🧩 Match dogs with complementary energy and play styles to minimize conflict</li>
              <li>🛑 Avoid pairing two rough players without monitoring — high arousal can escalate quickly</li>
              <li>🌟 Support gentle players by creating small, calm groups or enriching activities during peak energy times</li>
              <li>🎯 Rotate group dynamics thoughtfully — mixing play styles can keep sessions fresh but must be managed</li>
            </ul>
            <p><span class="highlight-blue">Smart group curation based on play style leads to healthier, more joyful playgroups.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Understanding and tracking play styles shows respect for each dog's individuality.</span> 
              At Pawsible, we honor their personalities while setting them up for success — one playful connection at a time.
            </p>
          </section>
        `
      },
      enrichmentBlock: {
        title: "Enrichment Program & Philosophy",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/enrichmentBlockTitle.png",
        body: `
          <section>
            <h3>🧠 Exercise Their Minds, Not Just Their Bodies</h3>
            <p>
              At Pawsible, play is vital — but <span class="highlight">structured enrichment</span> brings deeper benefits. 
              Our <strong>Enrichment Block</strong> is a daily opportunity to <span class="highlight-green">stimulate dogs mentally, build confidence, nurture emotional resilience, and strengthen the human-dog bond</span>.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Enrichment</h4>
            <ul>
              <li>🧠 Stimulate cognitive thinking and problem-solving skills</li>
              <li>🧘 Support emotional regulation and prevent overstimulation</li>
              <li>🛡️ Reduce reactivity and build adaptability</li>
              <li>🤝 Strengthen trust between dogs and staff</li>
            </ul>
            <p><span class="highlight-blue">A mentally and emotionally fulfilled dog is a calmer, safer, and happier daycare guest.</span></p>
          </section>
      
          <section>
            <h4>📅 2. Daily Enrichment Theme Schedule</h4>
            <ul>
              <li>🧠 <strong>Mindful Monday:</strong> Puzzles, snuffle mats, calm focus games</li>
              <li>🎯 <strong>Training Tuesday:</strong> Basic obedience, fun tricks, positive learning activities</li>
              <li>💦 <strong>Water Wednesday:</strong> Swimming, splash play, safe water retrieval games</li>
              <li>🌟 <strong>Thrill Thursday:</strong> Confidence-building challenges, obstacle courses, adventure play</li>
              <li>🐾 <strong>Friendship Friday:</strong> Gentle social games, partner bonding activities</li>
              <li>🏖️ <strong>Splash Saturday:</strong> Weekend water fun — pool time, splash parties, hose play</li>
              <li>🧘 <strong>Zen Sunday:</strong> Relaxation enrichment — sensory massages, quiet bonding, decompression walks</li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/enrichmentBlockImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
            <p><span class="highlight-green">This structured weekly rhythm balances brain, body, and social growth — while keeping daycare exciting, safe, and emotionally fulfilling year-round.</span></p>
          </section>
      
          <section>
            <h4>🔧 3. Principles of Successful Enrichment</h4>
            <ul>
              <li>🎯 <strong>Purpose Before Activity:</strong> Every session is chosen to fulfill a developmental goal — calming, stimulating, bonding, or confidence building.</li>
              <li>🌟 <strong>Quality Over Quantity:</strong> Short, focused sessions are more impactful than long, overstimulating ones.</li>
              <li>🧠 <strong>Adapt to the Dog:</strong> Tailor activities to the dog's age, energy level, emotional state, and personality.</li>
              <li>🔄 <strong>Rotate and Refresh:</strong> Vary activities weekly to keep curiosity and engagement high.</li>
              <li>🤝 <strong>Staff Are Leaders and Partners:</strong> Stay actively engaged — lead experiences with calm energy, celebrate participation, and coach gently.</li>
            </ul>
            <p><span class="highlight-green">At Pawsible, enrichment is a relationship-driven experience — not just entertainment. Every interaction builds trust, joy, and resilience.</span></p>
          </section>
      
          <section>
            <h4>📋 4. Best Practices for Enrichment</h4>
            <ul>
              <li>🌟 Reinforce participation with praise, positive attention, or small rewards (where appropriate)</li>
              <li>💬 Use upbeat, encouraging tones — enrichment must feel joyful, never pressured</li>
              <li>🧠 Adjust activities based on each dog’s confidence and emotional energy</li>
              <li>🔁 Maintain novelty — rotate enrichment types to prevent boredom and increase long-term engagement</li>
            </ul>
            <p><span class="highlight-green">Confidence grows fastest when dogs experience small, safe, consistent successes.</span></p>
          </section>
      
          <section>
            <h4>🚨 5. Safety Protocols for Enrichment</h4>
            <ul>
              <li>👀 Supervise all enrichment activities closely — no unattended sessions with puzzles, water, or agility setups</li>
              <li>🧹 Immediately remove broken, chewed, or unsafe items</li>
              <li>⚖️ Group dogs thoughtfully by play style, size, and toy drive to reduce tension and prevent guarding</li>
              <li>🛑 Pause and calmly reset activities if excitement levels become unsafe</li>
            </ul>
          </section>
      
          <section>
            <h4>🗂️ 6. Documentation & Paw Parent Updates</h4>
            <ul>
              <li>🗒️ Log participation notes for each dog: favorites, confidence levels, and notable wins</li>
              <li>💬 Highlight enrichment achievements in daily report cards ("Charlie conquered the mini obstacle course for Thrill Thursday!")</li>
              <li>📸 (Optional) Capture enrichment moments to deepen the emotional connection with paw parents</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">At Pawsible, enrichment isn't extra — it's essential.</span> 
              We intentionally nurture smarter, more adaptable, and more joyful dogs through thoughtful, heart-led engagement every single day.
            </p>
          </section>
        `
      },
      puppyProtocol: {
        title: "Puppy-Specific Protocols",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/puppyProtocolTitle.png",
        body: `
          <section>
            <h3>🐶 Gentle Foundations for Lifelong Confidence</h3>
            <p>
              Puppies are <span class="highlight">developing minds and bodies</span> — their experiences at daycare shape how they view the world for life. 
              At Pawsible, we handle puppies with <span class="highlight-green">extra care, patience, and intentional guidance</span> to ensure positive early socialization and safe play.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Special Considerations for Puppies</h4>
            <ul>
              <li>🍼 Puppies under 6 months must be fully vaccinated (as age-appropriate) before joining group play</li>
              <li>🧠 Understand that puppies have shorter attention spans, quicker fatigue, and sensitive physical development</li>
              <li>🛡️ Prioritize confidence building over correction — puppies need safe learning experiences, not discipline</li>
              <li>🧘 Limit overstimulation — frequent calm breaks help puppies process social learning</li>
            </ul>
            <p><span class="highlight-blue">A confident, social puppy today becomes a stable, happy adult dog tomorrow.</span></p>
          </section>
      
          <section>
            <h4>🔍 2. Group Placement & Supervision</h4>
            <ul>
              <li>👶 Whenever possible, puppies should play with other puppies, gentle adolescents, or tolerant adults</li>
              <li>🧸 Puppies should never be left unsupervised in groups — active guidance is required at all times</li>
              <li>📏 Monitor for signs of fear, shutdown, or overstimulation — adjust group or provide 1:1 time if needed</li>
              <li>🔄 Keep play sessions short and sweet — structured rotations between play, potty, rest, and enrichment</li>
            </ul>
          </section>
      
          <section>
            <h4>🛡️ 3. Puppy Play Style Expectations</h4>
            <ul>
              <li>🐾 Puppies often play with mouthy, exaggerated movements — supervise to ensure play stays friendly</li>
              <li>🚪 Give puppies safe "exit opportunities" if they become overwhelmed (open spaces, rest areas)</li>
              <li>🤝 Use positive reinforcement for appropriate play choices and polite greetings</li>
            </ul>
            <p><span class="highlight-green">Puppies aren't born knowing how to play — we coach them kindly into confident social dogs.</span></p>
          </section>
      
          <section>
            <h4>🧼 4. Cleanliness and Health Monitoring</h4>
            <ul>
              <li>🧼 Maintain heightened cleaning protocols in puppy play areas (puppy immune systems are still maturing)</li>
              <li>💩 Monitor stool consistency and log any diarrhea or unusual potty patterns immediately</li>
              <li>🩺 Report any coughing, sneezing, or signs of illness to management before reintroducing to groups</li>
            </ul>
          </section>
      
          <section>
            <h4>📝 5. Client Communication for Puppies</h4>
            <ul>
              <li>💬 Provide more detailed report card notes for puppies (energy levels, social confidence, potty progress)</li>
              <li>📅 Recommend shorter daycare visits for new puppies initially, then gradually extend time as they adapt</li>
              <li>🎯 Reinforce small wins to owners ("Luna practiced polite play and took great calm breaks today!")</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Puppies are not smaller adults — they are students of the world.</span> 
              At Pawsible, every interaction with a puppy is a chance to build resilience, trust, and lifelong joy.
            </p>
          </section>
        `
      },
      seniorPetCare: {
        title: "Senior Pet Care",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/seniorPetCareTitle.png",
        body: `
          <section>
            <h3>🧓 Cherishing Our Senior Guests with Care and Respect</h3>
            <p>
              At Pawsible, senior dogs deserve <span class="highlight">specialized attention, patience, and comfort</span>. 
              Older pets have earned their place as <span class="highlight-green">respected, valued members</span> of our community — and we adapt our care thoughtfully to meet their changing needs.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Understanding Senior Dog Needs</h4>
            <ul>
              <li>🧠 Cognitive changes: May have slower response times or mild confusion ("doggy dementia" symptoms)</li>
              <li>🏃 Physical changes: Decreased stamina, joint stiffness, vision or hearing decline</li>
              <li>💬 Emotional shifts: Some seniors become clingier, while others prefer more independence</li>
              <li>🛌 Rest becomes essential: Seniors need longer recovery periods after even mild activity</li>
            </ul>
            <p><span class="highlight-blue">At Pawsible, we meet seniors where they are — never force participation, always offer gentle options.</span></p>
          </section>
      
          <section>
            <h4>🛡️ 2. Senior Playgroup Adjustments</h4>
            <ul>
              <li>🌸 Offer small, calm playgroups or low-activity enrichment groups when possible</li>
              <li>🛋️ Provide easy access to soft resting areas away from high-energy dogs</li>
              <li>👀 Monitor carefully for fatigue — stop play if a senior shows signs of slowing down, stiffness, or withdrawal</li>
              <li>🤝 Allow seniors the choice to engage or relax freely without pressure</li>
            </ul>
            <p><span class="highlight-green">Freedom of choice = dignity. Let seniors set their own pace whenever safe to do so.</span></p>
          </section>
      
          <section>
            <h4>🔎 3. Health & Wellness Monitoring</h4>
            <ul>
              <li>🐾 Watch for signs of pain: limping, slow rising, stiffness, reluctance to jump or climb</li>
              <li>🩺 Observe for breathing changes, excessive panting, or coughing during mild activity</li>
              <li>🍽️ Note appetite changes, bathroom irregularities, or disorientation for parent reporting</li>
              <li>📋 Log all observations clearly in the daycare dashboard — early notes can help owners act proactively</li>
            </ul>
          </section>
      
          <section>
            <h4>📋 4. Enrichment for Senior Dogs</h4>
            <ul>
              <li>🧠 Focus on low-impact sensory enrichment: snuffle mats, gentle touch games, scent exploration</li>
              <li>🎯 Keep training short, positive, and pressure-free — celebrate engagement, not perfection</li>
              <li>🎾 Offer slow-paced fetch games, massage sessions, or simple movement puzzles</li>
              <li>🌿 Prioritize quality of experience over quantity of activity</li>
            </ul>
            <p><span class="highlight-blue">At Pawsible, enrichment isn’t just about energy — it’s about emotional wellbeing at every life stage.</span></p>
          </section>
      
          <section>
            <h4>💬 5. Communication with Senior Dog Paw Parents</h4>
            <ul>
              <li>💬 Share personalized updates on their comfort, engagement, and wellness in report cards</li>
              <li>☎️ Notify immediately if health concerns arise during daycare</li>
              <li>🌟 Highlight small wins ("Bailey enjoyed a cozy sun nap and gentle snuffle mat time today!")</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Caring for senior dogs is a privilege.</span> 
              At Pawsible, we honor our older guests with patience, dignity, and customized care — helping them live their golden years with joy and comfort.
            </p>
          </section>
        `
      },      
      feedingMedication: {
        title: "Feeding & Medication Protocols",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/feedingMedicationTitle.png",
        body: `
          <section>
            <h3>🥣 Consistency and Caution: Feeding and Medication at Pawsible</h3>
            <p>
              At Pawsible, <span class="highlight">accuracy, hygiene, and careful documentation</span> are critical when handling any feeding or medication routines. 
              Proper management protects the health and trust of our daycare guests — and ensures client confidence.
            </p>
          </section>
      
          <section>
            <h4>🍽️ 1. Feeding Protocols</h4>
            <ul>
              <li>📋 Always verify feeding instructions from the client profile before serving</li>
              <li>🛡️ Separate feeding areas from playgroups to avoid food guarding or accidents</li>
              <li>🕰️ Stick closely to food and feeding schedules provided by paw parents — note any deviations immediately</li>
              <li>🧼 Sanitize all feeding bowls thoroughly after each use</li>
              <li>⚠️ Monitor each dog during and after eating — log any vomiting, refusal to eat, or abnormal behavior</li>
            </ul>
            <p><span class="highlight-blue">Meal times should be calm, organized, and individually supervised for each dog receiving food during their stay.</span></p>
          </section>
      
          <section>
            <h4>💊 2. Medication Administration Protocols</h4>
            <ul>
              <li>🗂️ Only administer medications that have clear written instructions from the owner and/or veterinarian</li>
              <li>📋 Double-check:
                <ul>
                  <li>Dog’s name</li>
                  <li>Medication name</li>
                  <li>Correct dosage</li>
                  <li>Time of administration</li>
                </ul>
              </li>
              <li>🧤 Wash hands thoroughly before and after handling any medications</li>
              <li>🕰️ Administer medications at the scheduled times — if delayed, note the time and reason clearly</li>
              <li>🧾 Record each administration immediately after giving the medication (including initials of staff member)</li>
              <li>🛡️ If a dog refuses a medication, do not force — notify management and the owner immediately</li>
            </ul>
            <p><span class="highlight-red">Accuracy and documentation are legally required — treat medication handling as a top safety priority every time.</span></p>
          </section>
      
          <section>
            <h4>⚠️ 3. Allergy Awareness and Food Sensitivities</h4>
            <ul>
              <li>🚫 Never share treats, chews, or food between dogs without written owner permission</li>
              <li>🔍 Always check for food allergies or sensitivities listed in the client profile before offering anything edible</li>
              <li>🛑 Immediately remove any shared food bowls or water dishes during allergy alerts</li>
            </ul>
            <p><span class="highlight-green">Protecting allergy-sensitive dogs is a team responsibility — when in doubt, ask first, feed later.</span></p>
          </section>
      
          <section>
            <h4>📋 4. Record Keeping & Parent Communication</h4>
            <ul>
              <li>🗂️ Maintain clear records for all feedings and medication administrations in the daycare dashboard</li>
              <li>💬 Notify paw parents immediately if any feeding or medication issues occur (missed doses, refusal, vomiting)</li>
              <li>📝 Include positive updates on meal participation in daily report cards when appropriate</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Feeding and medication care isn't just routine — it's an extension of trust.</span> 
              At Pawsible, we handle every meal, every pill, and every health note with professionalism, empathy, and precision.
            </p>
          </section>
        `
      },
      behaviorIncident: {
        title: "Behavioral Incident",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/behaviorIncidentTitle.png",
        body: `
          <section>
            <h3>🚨 Calm, Clear, and Consistent: Managing Behavioral Incidents</h3>
            <p>
              At Pawsible, behavioral incidents are handled with <span class="highlight">professionalism, empathy, and proactive communication</span>. 
              Our goal is to <span class="highlight-green">minimize risk, de-escalate calmly, and support both the dog and the daycare community</span> through thoughtful action.
            </p>
          </section>
      
          <section>
            <h4>🛑 1. Defining a Behavioral Incident</h4>
            <ul>
              <li>🐕 Escalated fights, bites, or physical altercations between dogs</li>
              <li>👃 Excessive bullying, stalking, or targeting behavior</li>
              <li>😨 Extreme fear responses (shutting down, snapping, defensive aggression)</li>
              <li>🧍‍♂️ Staff-directed aggression (growling, biting, severe guarding)</li>
              <li>🛡️ Any injury-causing or serious stress event requiring intervention</li>
            </ul>
            <p><span class="highlight-red">Not all spats are "incidents" — normal corrections (brief vocal warnings or minor posturing) are part of dog communication and don't require full reports unless they escalate.</span></p>
          </section>
      
          <section>
            <h4>🚨 2. Immediate Action During an Incident</h4>
            <ul>
              <li>🗣️ Stay calm — never yell or create added chaos</li>
              <li>🤚 Use trained redirection techniques: loud clap, body block, verbal interrupter ("Hey!")</li>
              <li>🐕 Quickly and calmly separate dogs if physical contact occurs</li>
              <li>🧘 Guide dogs to individual calm zones immediately after separation</li>
              <li>🩺 Assess both dogs for visible injuries quietly before contacting management</li>
            </ul>
            <p><span class="highlight-green">Calm leadership lowers adrenaline faster and prevents escalation.</span></p>
          </section>
      
          <section>
            <h4>📋 3. Incident Documentation and Reporting</h4>
            <ul>
              <li>📝 Fill out a detailed incident report immediately after situation is safe</li>
              <li>📋 Include:
                <ul>
                  <li>Dogs involved</li>
                  <li>Sequence of events (what happened before, during, after)</li>
                  <li>Staff actions taken</li>
                  <li>Injury assessments (even if no injuries)</li>
                </ul>
              </li>
              <li>🧠 Stick to facts only — no emotional language or assumptions</li>
              <li>🧑‍💼 Submit report to manager for review before any client communication</li>
            </ul>
          </section>
      
          <section>
            <h4>💬 4. Communication with Paw Parents</h4>
            <ul>
              <li>☎️ Management or senior staff will contact the owner the same day</li>
              <li>🎯 Focus communication on transparency, empathy, and next steps</li>
              <li>🌟 If minor: Share in daily report cards with neutral, factual wording ("Luna had a minor scuffle today but was calmly separated and is doing well.")</li>
              <li>🚨 If moderate or major: Direct call required plus a written incident follow-up</li>
            </ul>
            <p><span class="highlight-blue">Empathy, honesty, and professionalism build parent trust even during difficult conversations.</span></p>
          </section>
      
          <section>
            <h4>🔄 5. Post-Incident Review and Care Plan</h4>
            <ul>
              <li>🧠 Manager and staff will debrief to analyze triggers and preventive strategies</li>
              <li>⚖️ Dogs involved may have temporary modified playgroup assignments or decompression periods</li>
              <li>📈 Staff may recommend individualized enrichment plans, smaller groups, or trial reintroductions</li>
              <li>🛡️ If necessary: Recommend private behavior consultations or training support to owners</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Behavioral incidents don't define a dog — how we respond defines the experience.</span> 
              At Pawsible, we turn challenges into opportunities for better leadership, safer playgroups, and stronger human-dog trust.
            </p>
          </section>
        `
      },
      quietTimeSetup: {
        title: "Quiet Time Setup & Supervision",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/quietTimeSetupTitle.png",
        body: `
          <section>
            <h3>🧘‍♂️ Recharge and Reset: Quiet Time at Pawsible</h3>
            <p>
              At Pawsible, <span class="highlight">quiet time</span> is an essential part of a balanced daycare day. 
              Structured downtime helps dogs <span class="highlight-green">reset emotionally, prevent overstimulation, and physically recharge</span> for safer, happier playgroups.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Quiet Time</h4>
            <ul>
              <li>🧠 Supports emotional regulation after high-energy activities</li>
              <li>💪 Reduces risk of overstimulation, resource guarding, or conflict escalation</li>
              <li>🌿 Gives young, senior, and shy dogs a critical break to decompress</li>
              <li>😴 Helps maintain a peaceful overall group dynamic throughout the day</li>
            </ul>
            <p><span class="highlight-blue">Downtime is proactive care — it prevents problems before they start.</span></p>
          </section>
      
          <section>
            <h4>🛏️ 2. Quiet Time Setup</h4>
            <ul>
              <li>🛋️ Dim lights slightly or play soft background music to encourage relaxation</li>
              <li>🛏️ Provide individual rest spaces: cots, soft beds, separated quiet zones</li>
              <li>🚪 Separate excitable dogs into smaller pods or individual spaces when needed</li>
              <li>🥤 Fresh water available at all times</li>
            </ul>
            <p><span class="highlight-green">Create an environment that invites calmness naturally — comfort and security are key.</span></p>
          </section>
      
          <section>
            <h4>🧘‍♂️ 3. Quiet Time Supervision</h4>
            <ul>
              <li>👀 Monitor the room quietly but attentively — minimize loud voices or disruptive movements</li>
              <li>😴 Allow dogs to choose how they rest: curled up together, solo on beds, or quietly wandering if appropriate</li>
              <li>🛑 Redirect or separate dogs that cannot settle without using harsh corrections</li>
              <li>🐾 Be patient — some dogs need gentle guidance (lead to bed, quiet petting) to relax</li>
            </ul>
          </section>
      
          <section>
            <h4>⏰ 4. Quiet Time Timing</h4>
            <ul>
              <li>🕰️ Typically scheduled mid-day, after morning playgroups and before afternoon activities</li>
              <li>🧠 Length may vary from 30 minutes to 1 hour depending on group energy and age mix</li>
              <li>🎯 Stay flexible — young puppies, seniors, or shy dogs may need additional or earlier quiet breaks</li>
            </ul>
            <p><span class="highlight-blue">Timing is about the dogs’ needs, not the clock — leadership means reading the room.</span></p>
          </section>
      
          <section>
            <h4>💬 5. Communicating Quiet Time to Paw Parents</h4>
            <ul>
              <li>🧸 Highlight quiet time in report cards as part of Pawsible's holistic care approach ("Luna enjoyed a cozy midday break after an energetic morning play!")</li>
              <li>🌟 Reassure parents that structured rest is part of promoting well-rounded health and happiness</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Rest is not a luxury — it's a necessity for emotional and physical wellbeing.</span> 
              Quiet time helps every dog at Pawsible stay balanced, joyful, and ready to thrive in group settings.
            </p>
          </section>
        `
      },      
      clientAlertSystem: {
        title: "Client Alert System",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/clientAlertSystemTitle.png",
        body: `
          <section>
            <h3>📣 Keeping Paw Parents Informed: Our Client Alert System</h3>
            <p>
              At Pawsible, <span class="highlight">clear, timely communication</span> builds trust with every client. 
              Our <strong>Client Alert System</strong> ensures that <span class="highlight-green">important updates about a pet's health, behavior, or incidents</span> are communicated professionally, promptly, and with empathy.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. What Triggers a Client Alert?</h4>
            <ul>
              <li>🩺 Health concerns: Vomiting, diarrhea, limping, coughing, lethargy, injury signs</li>
              <li>🚨 Behavioral incidents: Fights, bites, significant fear reactions, staff-directed aggression</li>
              <li>⚠️ Environmental exposures: Exposure to accidents, unsafe conditions, or emergency evacuations</li>
              <li>🛑 Emergency deviations: Missed medication doses, sudden allergy reactions, ingestion of non-food items</li>
            </ul>
            <p><span class="highlight-red">Always alert management immediately if any situation could potentially affect a pet's health, safety, or client trust.</span></p>
          </section>
      
          <section>
            <h4>💬 2. Alert Communication Process</h4>
            <ul>
              <li>🧑‍💼 Step 1: Notify supervisor or manager as soon as incident occurs</li>
              <li>🗂️ Step 2: Fill out internal notes in the daycare dashboard clearly and factually</li>
              <li>☎️ Step 3: Manager or designated senior staff member contacts client via call or text (depending on severity)</li>
              <li>📝 Step 4: Written incident notes sent via email or app update for client records, if appropriate</li>
            </ul>
            <p><span class="highlight-blue">Staff should never alert a client directly unless instructed by management — we maintain consistent, polished communication channels.</span></p>
          </section>
      
          <section>
            <h4>📋 3. Tone and Language Guidelines</h4>
            <ul>
              <li>💬 Stay factual, neutral, and empathetic — avoid emotional exaggerations or blame</li>
              <li>🌟 Focus on the pet’s current condition and next steps (“Luna was separated quickly and is resting comfortably.”)</li>
              <li>🧠 Avoid speculating about causes unless verified ("We observed, but we don't diagnose.")</li>
              <li>🤝 Reassure clients that their pet’s wellbeing is our top priority</li>
            </ul>
            <p><span class="highlight-green">Transparency + professionalism = stronger trust and long-term loyalty.</span></p>
          </section>
      
          <section>
            <h4>📈 4. Logging and Follow-Up</h4>
            <ul>
              <li>📋 Always log alerts in the dog's client dashboard — even if no action from parent is required</li>
              <li>🔁 Schedule check-ins after a behavioral or health alert to monitor progress and update parents if needed</li>
              <li>📝 Include outcomes in the dog’s permanent profile for future staff reference (e.g., "sensitive to heat after 30 min play")</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Great care isn't just what we do — it's how well we communicate about it.</span> 
              At Pawsible, keeping paw parents informed with clarity and compassion is just as important as the care we deliver.
            </p>
          </section>
        `
      },      
      daycarePhotoSnapshots: {
        title: "Photo/Video Snapshots for Daycare Guests",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/daycarePhotoSnapshotsTitle.png",
        body: `
          <section>
            <h3>📸 Capturing Joy: Content Snapshots for Daycare Guests</h3>
            <p>
              At Pawsible, <span class="highlight">photo and video updates aren't just cute — they're powerful</span>. 
              Thoughtful snapshots help <span class="highlight-green">deepen parent trust, showcase daily enrichment, and celebrate each dog's unique daycare journey</span>.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Content Snapshots</h4>
            <ul>
              <li>🐾 Strengthen emotional connection between paw parents and their pets’ daycare experience</li>
              <li>📈 Increase loyalty by offering visible, personalized touches</li>
              <li>🌟 Highlight participation in enrichment activities, group play, or milestone moments</li>
              <li>📚 Support storytelling in daily report cards and social media when appropriate</li>
            </ul>
            <p><span class="highlight-blue">A well-timed snapshot creates lasting memories — and shows that we see each dog as an individual, not a number.</span></p>
          </section>
      
          <section>
            <h4>📋 2. When to Capture Moments</h4>
            <ul>
              <li>👋 Welcoming moments when a new dog joins the group (first greetings, relaxed play, making early connections)</li>
              <li>🎯 During enrichment activities (Mindful Monday, Thrill Thursday, Splash Saturday, etc.)</li>
              <li>🐶 During special or noteworthy moments (new friendship formed, learning a new trick, first pool swim!)</li>
              <li>🎉 On birthdays, holiday events, or theme days (with appropriate decor/safety)</li>
              <li>🧠 If a shy or nervous dog makes visible social progress ("Charlie finally joined the small dog group!")</li>
            </ul>
            <p><span class="highlight-green">Capture genuine and positive moments — never staged or forced if dogs are uncomfortable.</span></p>
          </section>
      
          <section>
            <h4>📸 3. Best Practices for Taking Snapshots</h4>
            <ul>
              <li>🔆 Use natural light whenever possible — avoid flash photography (can startle dogs)</li>
              <li>👀 Focus on clear, happy expressions or engaged body language</li>
              <li>📏 Avoid capturing of dogs crowding water bowls, gates, or fencing (safety first impressions)</li>
              <li>🎯 <span class="highlight-red">Take 1–2 good shots</span> rather than flooding with multiple low-quality images - aim for 15-20 for daily</li>
              <li>🧼 <span class="highlight-green">Ensure background areas are tidy (no messes visible!)</span></li>
            </ul>
          </section>
      
          <section>
            <h4>📲 4. Uploading and Sharing Guidelines</h4>
            <ul>
              <li>🖼️ Upload to the team chat under Daycare</li>
              <li>💬 Include short captions on enrichment highlights ("Max loved exploring the ball pit today!")</li>
              <li>🛡️ Only staff-approved images may be used for public social media posts</li>
            </ul>
            <p><span class="highlight-blue">Photo documentation is a professional service — treat it with the same pride as hands-on care.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Every photo tells a story — and every story deepens trust.</span> 
              At Pawsible, we don't just care for dogs — we celebrate them, capture their joy, and share their daycare journey with the people who love them most.
            </p>
          </section>
        `
      },      
      dailyReportCards: {
        title: "Daily Report Cards & Parent Communication",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/dailyReportCardsTitle.png",
        body: `
          <section>
            <h3>📝 Building Trust Through Thoughtful Updates</h3>
            <p>
              At Pawsible, <span class="highlight">daily report cards</span> are not just updates — they are a reflection of our <span class="highlight-green">care, attention, and expertise</span>. 
              Consistent, clear communication strengthens client loyalty, highlights each dog’s experience, and reinforces why paw parents trust us.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Daily Report Cards</h4>
            <ul>
              <li>🐾 Share highlights of the dog's day: playgroups, enrichment activities, energy levels</li>
              <li>🧠 Communicate any minor health or behavioral observations</li>
              <li>🌟 Reinforce the value of structured care, not just free play</li>
              <li>🤝 Deepen emotional connection with paw parents through positivity and transparency</li>
            </ul>
            <p><span class="highlight-blue">Great report cards make pet parents feel involved, reassured, and excited to return.</span></p>
          </section>
      
          <section>
            <h4>🖊️ 2. Core Elements of a Good Report Card</h4>
            <ul>
              <li>📸 Optional: Attach a candid photo if available (happy play, enrichment activity, cute moment)</li>
              <li>📋 Key Points to Include:
                <ul>
                  <li>Playgroup behavior (friendly, shy, confident, gentle, energetic)</li>
                  <li>Enrichment participation (Water Wednesday, Thrill Thursday, Mindful Monday, etc.)</li>
                  <li>Notable interactions (made new friends, favorite activities)</li>
                  <li>Health check notes (e.g., normal energy, minor tiredness, etc.)</li>
                  <li>Staff positivity highlight (personal wins, proud moments)</li>
                </ul>
              </li>
              <li>🧠 Keep it factual, personalized, and upbeat — no generic copy-paste messaging</li>
            </ul>
          </section>
      
          <section>
            <h4>💬 3. Language and Tone Guidelines</h4>
            <ul>
              <li>🌟 Lead with positives ("Luna had a wonderful time exploring the sensory course today!")</li>
              <li>🔵 Use neutral, factual tone for any concerns ("Luna took a few extra breaks today to cool off.")</li>
              <li>🧠 Avoid labeling dogs negatively ("stubborn," "difficult," etc.) — focus on behaviors, not personality judgment</li>
              <li>🤗 Always end on a friendly, upbeat note to leave parents smiling</li>
            </ul>
            <p><span class="highlight-green">Our words should reflect care, expertise, and optimism — even when delivering delicate updates.</span></p>
          </section>
      
          <section>
            <h4>🛡️ 4. Handling Behavioral or Health Observations</h4>
            <ul>
              <li>⚖️ Minor observations (fatigue, overexcitement, nervousness) can be included gently in the report card</li>
              <li>🚨 Major incidents (injury, fight, illness symptoms) require a direct manager call first — not just a note</li>
              <li>📋 Use objective language and facts only — no assumptions or medical diagnoses</li>
            </ul>
          </section>
      
          <section>
            <h4>📈 5. Best Practices for Consistency</h4>
            <ul>
              <li>🗓️ Aim for same-day report card completion (before or at pick-up)</li>
              <li>🧹 Proofread for typos, especially pet names and behavior details</li>
              <li>📝 If multiple staff contributed to care, coordinate notes for a full picture of the day</li>
              <li>🎯 Make each card feel personalized — show we know and love their dog individually</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Report cards aren't just updates — they're tiny windows into the joyful care we provide.</span> 
              Every well-crafted report builds lasting trust, reinforces professionalism, and celebrates the relationship between Pawsible, our guests, and their families.
            </p>
          </section>
        `
      },      
      daycareCheckout: {
        title: "End-of-Day Check-Out Routine",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/daycareCheckoutTitle.png",
        body: `
          <section>
            <h3>🏁 Wrapping Up with Care: End-of-Day Check-Out Routine</h3>
            <p>
              At Pawsible, <span class="highlight">the last impression is just as important as the first</span>. 
              Our check-out process ensures every dog leaves safely, happily, and that <span class="highlight-green">paw parents feel confident and cared for</span> at every pickup.
            </p>
          </section>
      
          <section>
            <h4>🛁 1. Pre-Check-Out Pet Prep</h4>
            <ul>
              <li>🪮 Quick brush-out if coat is messy or has debris</li>
              <li>🧼 Wipe down paws and coat if dirty (especially after splash days or rainy weather)</li>
              <li>🧽 Clean up any eye or facial debris gently for a polished handoff</li>
              <li>🚰 Offer a fresh drink of water before departure</li>
            </ul>
            <p><span class="highlight-blue">We want every dog to go home looking (and feeling) fresh, happy, and well cared for.</span></p>
          </section>
      
          <section>
            <h4>📋 2. Behavior & Health Final Check</h4>
            <ul>
              <li>👀 Quick physical check for any signs of injury, irritation, limping, or abnormal behavior</li>
              <li>🧠 Mental check: Is the dog relaxed, calm, and ready to go home?</li>
              <li>🛑 Flag any concerns to the shift lead or manager immediately before check-out</li>
            </ul>
          </section>
      
          <section>
            <h4>💬 3. Communicating with Paw Parents at Pick-Up</h4>
            <ul>
              <li>🤗 Greet parents warmly and positively — pick-up is a celebratory moment!</li>
              <li>📝 Share quick highlights of the dog's day (playgroups, enrichment activities, positive behaviors)</li>
              <li>📋 Mention anything notable politely and factually (e.g., "Luna had a blast during Water Wednesday but got a little tired early!")</li>
              <li>🧠 Keep tone upbeat and reassuring — especially if reporting minor behavioral or health notes</li>
            </ul>
            <p><span class="highlight-green">Every conversation is an opportunity to build client loyalty through transparency, expertise, and kindness.</span></p>
          </section>
      
          <section>
            <h4>📝 4. Final Documentation and Departure</h4>
            <ul>
              <li>📲 Mark the dog officially checked out in the daycare dashboard immediately after handoff</li>
              <li>🔁 Log any final notes that may impact the dog's next visit (fatigue, behavioral improvements, health updates)</li>
              <li>📸 Optional: Capture quick photos for report card or post-visit updates if part of service</li>
            </ul>
          </section>
      
          <section>
            <h4>🚪 5. Safety Check at Pick-Up</h4>
            <ul>
              <li>🔒 Ensure collars/harnesses are secure before handing off to owner</li>
              <li>🚗 Double-check gate and lobby safety — prevent loose dogs or rushed handoffs</li>
              <li>🧑‍🤝‍🧑 Confirm identity if unfamiliar pick-up person (use security questions if needed)</li>
            </ul>
            <p><span class="highlight-red">A calm, careful, secure handoff is non-negotiable — the safety of each dog depends on it.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">End-of-day routines aren’t just about logistics — they’re about gratitude, trust, and professionalism.</span> 
              At Pawsible, we send every pet home with care, pride, and joy — leaving paw parents excited to return again and again.
            </p>
          </section>
        `
      },
      daycareCleaning: {
        title: "Sanitation & Cleaning Protocols",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/daycareCleaningTitle.png",
        body: `
          <section>
            <h3>🧼 Clean Spaces, Happy Faces: Daycare Sanitation Standards</h3>
            <p>
              At Pawsible, <span class="highlight">cleanliness is non-negotiable</span>. 
              A spotless environment protects the <span class="highlight-green">health, safety, and emotional comfort</span> of every dog — and reflects the professionalism our paw parents trust.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Why Sanitation Matters</h4>
            <ul>
              <li>🩺 Reduces the spread of illnesses (kennel cough, parasites, viruses)</li>
              <li>🛡️ Prevents buildup of allergens, dirt, and harmful bacteria</li>
              <li>🐾 Creates a safe, welcoming, odor-free environment for dogs and paw parents alike</li>
              <li>🌟 Reinforces Pawsible’s brand commitment to premium care and safety</li>
            </ul>
            <p><span class="highlight-blue">Clean spaces are calm spaces — dogs relax more easily when their environment is healthy and cared for.</span></p>
          </section>
      
          <section>
            <h4>🧹 2. Daily Cleaning</h4>
            <ul>
              <li>🚽 Pick up and sanitize any potty accidents immediately</li>
              <li>🧴 Wipe down water bowls, feeding areas, and enrichment equipment after use</li>
              <li>🧽 Spot mop floors throughout the day (after accidents, muddy paws, spills)</li>
              <li>🌬️ Ensure all ventilation systems and fans are operating for air flow</li>
              <li>🛏️ Wash used bedding if soiled or assigned for that day</li>
              <li>💦 Refill water bowls frequently with fresh, clean water</li>
            </ul>
            <p><span class="highlight-green">Small cleaning actions throughout the day prevent bigger sanitation risks later.</span></p>
          </section>
      
          <section>
            <h4>🧼 3. Deep Cleaning (Mon, Wed & Sat)</h4>
              <ul>
                  <li>🔒 Secure all daycare dogs to designated zones</li>
                  <li>🚿 Water down the faux grass using the foaming disinfectant sprayer</li>
                  <li>🧽 Scrub with the scrape broom to remove debris and waste</li>
                  <li>🌬️ Roll up the artificial turf to dry overnight</li>
                  <li>🕳️ Drain all standing water fully</li>
                  <li>🧰 Empty and rinse the turf drain filter basket</li>
                  <li>💨 Turn on fans & AC to dry the floor for ventilation</li>
                  <li>🧹 Sweep and mop lobby & bathroom floors</li>
                  <li>🧼 Sanitize crates, water bowls, and food stations not in active use</li>
                  <li>🧺 Run a final load of laundry if baskets are full — rotate clean towels</li>
                  <li>🧴 Restock items in all areas</li>
              </ul>
            <p><span class="highlight-blue">Full resets create a fresh, clean start for our guests.</span></p>
          </section>
      
          <section>
            <h4>🧪 4. Approved Cleaning Products</h4>
            <ul>
              <li>🛡️ Only use pet-safe, veterinary-grade disinfectants approved by Pawsible management</li>
              <li>🚫 Never use harsh chemicals (like bleach or ammonia) in dog-occupied areas during operating hours</li>
              <li>🌿 Favor eco-friendly, low-odor solutions when possible</li>
              <li>📋 Follow manufacturer instructions for dilution, contact time, and ventilation needs carefully</li>
            </ul>
          </section>
      
          <section>
            <h4>📋 5. Staff Hygiene Protocols</h4>
            <ul>
              <li>🧼 Wash hands after handling cleaning equipment, waste, or before handling food/medications</li>
              <li>🧤 Use gloves when administering medications or doing deep cleaning</li>
              <li>🩺 Maintain clean uniforms — report for shifts in fresh attire if heavily soiled during the day</li>
            </ul>
            <p><span class="highlight-green">Staff hygiene = guest safety. Our care for dogs starts with caring for ourselves, too.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">At Pawsible, cleanliness is love in action.</span> 
              Every wipe, mop, and scrub tells our dogs — and their parents — that their health and happiness matter to us.
            </p>
          </section>
        `
      }
}